import { defineStore } from "pinia";
import type { DossierModel } from "@/models/domain/DossierModel";
import { nextTick, ref, watch } from "vue";
import { isEqual, merge } from "es-toolkit";
import { migrateFiledata } from "@loanlink-nl/migration";
import { newCustomer } from "@/helpers/inventarisation/customers";
import { useDebounceFn, useRefHistory } from "@vueuse/core";
import { useZero } from "@/composables/useZero";
import { useDossierStore } from "./useDossierStore";
import { mutators } from "@loanlink-nl/zero";
import { useLoadingStore } from "./useLoadingStore";
import { useMainStore } from "./useMainStore";
import { DateTime } from "luxon";

function getInitialData(
	financingDefaults?: {
		notarisLevering?: number;
		notarisHypotheek?: number;
		taxatiekosten?: number;
		kostenHypotheekadvies?: number;
	} | null,
): DossierModel {
	const defaultPasseerdatum = DateTime.now()
		.plus({ months: 2 })
		.startOf("month")
		.toISODate();

	const defaultRekendatum = DateTime.now().year.toString();

	return {
		financieringsOpzet: {
			passeerdatum: defaultPasseerdatum,
			rekendatum: defaultRekendatum,
			eigenMiddelenInputType: "auto",
			aankoopkosten: financingDefaults
				? {
						notarisLevering: financingDefaults.notarisLevering,
						notarisHypotheek: financingDefaults.notarisHypotheek,
						taxatiekosten: financingDefaults.taxatiekosten,
						kostenHypotheekadvies: financingDefaults.kostenHypotheekadvies,
					}
				: undefined,
		},
		klantgegevens: {},
		mortgage: {
			mortgageParts: [],
		},
		existingMortgages: [],
		objectData: {
			isRented: false,
			verduurzamingBesproken: true,
		},
		maximaleHypotheek: {},
		rentepercentages: {},
		customers: [newCustomer()],
		income: {
			byCustomer: [],
		},
		liabilities: {
			byCustomer: [],
		},
		currentObjects: {
			hasOwnedProperty: undefined,
			objects: [],
		},
		assets: {
			byCustomer: [],
		},
		retirementAdvice: {
			household: {},
			byCustomer: [],
		},
	};
}

export const useDossierDataStore = defineStore("dossierData", () => {
	async function loadFileData(files: FileList | null): Promise<void> {
		if (!files || files?.length === 0) {
			return;
		}

		if (
			!confirm(
				"De gegevens in dit dossier worden overschreven als u dit bestand importeert. Doorgaan met importeren?",
			)
		) {
			return;
		}

		const file = files[0];
		if (!file) {
			return;
		}

		const dataString = await file.text();
		const savefile = JSON.parse(dataString);
		await setData(savefile);
	}

	function $reset() {
		pause();
		clear();

		data.value = getInitialData();
		lastCommittedValue = undefined;
	}

	const data = ref(getInitialData());
	let lastCommittedValue: DossierModel | undefined;

	const { history, isTracking, pause, resume, clear } = useRefHistory(data, {
		deep: true,
		clone: true,
		shouldCommit: (_oldVal, newVal) => {
			const clonedNew = JSON.parse(JSON.stringify(newVal));
			const shouldCommit = !isEqual(lastCommittedValue, clonedNew);

			if (shouldCommit) {
				lastCommittedValue = clonedNew;
			}

			return shouldCommit;
		},
	});

	async function setData(dataToSet: any = {}, initial = false) {
		const oldVersion = dataToSet?.version;
		const file = JSON.parse(JSON.stringify(dataToSet ?? {}));

		migrateFiledata(file);

		const mainStore = useMainStore();
		const agencyDefaults = mainStore.agency?.financingDefaults as
			| {
					notarisLevering?: number;
					notarisHypotheek?: number;
					taxatiekosten?: number;
					kostenHypotheekadvies?: number;
			  }
			| undefined;

		pause();
		data.value = merge(getInitialData(agencyDefaults), file.data ?? {});

		if (initial) {
			clear();
			lastCommittedValue = JSON.parse(JSON.stringify(data.value));
		}

		await nextTick();

		if (file.version !== oldVersion || file.version === undefined) {
			const dossierStore = useDossierStore();
			if (dossierStore.selectedDossier?.id) {
				saveDebounced(dossierStore.selectedDossier.id);
			}
		}

		resume();
	}

	async function save(dossierId: string) {
		return useZero()
			.value.mutate(
				mutators.dossier.update({
					id: dossierId,
					data: {
						data: JSON.parse(JSON.stringify(data.value)),
						version: __APP_VERSION__,
					},
					modifiedAt: Date.now(),
					mutationId: crypto.randomUUID(),
				}),
			)
			.server.then(() => {
				loadingStore.unsavedChanges = false;
			});
	}
	const saveDebounced = useDebounceFn(save, 1000);

	const loadingStore = useLoadingStore();
	watch(history, () => {
		if (!isTracking.value) {
			return;
		}

		loadingStore.unsavedChanges = true;
		const dossierStore = useDossierStore();
		if (dossierStore.selectedDossier?.id) {
			saveDebounced(dossierStore.selectedDossier.id);
		}
	});

	return {
		data,
		setData,
		saveImmediate: save,
		save: saveDebounced,
		history,
		loadFileData,
		$reset,
	};
});
